import { Stack } from "@mui/joy";
import React from "react";
import { SelectButtonCircleV2 } from "components/common/SelectButtonCircleV2";
import { SelfCheckQuestion } from "components/common/SelfCheckQuestion";
import { useQnAOnboardingPromotion } from "../../../../hooks/useQnAOnboardingPromotion";
import {
  MktVersionTypes,
  versionToPathPrefix,
} from "../../../../components/mkt/mktVersionUtils";

export interface QnaAnswerOption {
  id: number;
  text: string;
}

export interface Question {
  questionId: number;
  main: string;
  left: string;
  right: string;
}

const questionList: Question[] = [
  {
    questionId: 1,
    main: "내가 원하는 것은",
    left: "평온한\n마음이다",
    right: "건강한\n라이프스타일이다",
  },
  {
    questionId: 2,
    main: "마음을 치료하는 것은",
    left: "삶 전반에\n영향을 준다",
    right: "일부에만\n영향을 준다",
  },
  {
    questionId: 3,
    main: "내 마음이 안정적이라면",
    left: "스스로를 더\n사랑할 것이다",
    right: "그와 무관하게\n사랑할 것이다",
  },
  {
    questionId: 4,
    main: "내가 겪는 심리적 괴로움은",
    left: "항상\n내 곁에 있다",
    right: "특정한 트리거로\n발생한다",
  },
  {
    questionId: 5,
    main: "나의 변화를 가로막는 것은",
    left: "나의 게으름과\n의지 부족이다",
    right: "올바른 방법과\n안내의 부재다",
  },
  {
    questionId: 6,
    main: "나의 괴로움을 해결하기 위해서는",
    left: "위로와 공감이\n필요하다",
    right: "원인 분석과\n훈련이 필요하다",
  },
  {
    questionId: 7,
    main: "내 마음을 힘들게 하는 것은",
    left: "외적인\n요인 때문이다",
    right: "내적인\n요인 때문이다",
  },
  {
    questionId: 8,
    main: "건강한 마음을 갖는 것은",
    left: "삶의 궁극적인\n목표다",
    right: "삶의 여러\n목표 중 하나다",
  },
  {
    questionId: 9,
    main: "인간의 마음은",
    left: "알 수 없는\n미지의 영역이다",
    right: "원리를 알면\n다룰 수 있다",
  },
  {
    questionId: 10,
    main: "전문가의 도움을 받는 것은",
    left: "치부가 드러나\n꺼려진다",
    right: "빠르고 확실한\n선택이다",
  },
  {
    questionId: 11,
    main: "마음을 치료하고자 하는 나의 의지는",
    left: "항상 일정하게\n유지된다",
    right: "시간이 흐르며\n들쑥날쑥하다",
  },
  {
    questionId: 12,
    main: "마음이 건강하다는 것은",
    left: "고통을 잘\n이겨낸다는 것이다",
    right: "고통이\n없다는 것이다",
  },
  {
    questionId: 13,
    main: "비약물 심리치료 프로그램은 내게",
    left: "새로운 것을\n알려줄 것이다",
    right: "아무것도 알려주지\n못할 것이다",
  },
  {
    questionId: 14,
    main: "그동안 접했던 심리치료 정보들은",
    left: "삶에 진짜\n변화를 주었다",
    right: "잠깐의 위안만\n주었다",
  },
  {
    questionId: 15,
    main: "나의 인생에서 정신건강은",
    left: "변두리에 있는\n후순위다",
    right: "모든 것의\n중심이다",
  },
  {
    questionId: 16,
    main: "자가치료법을 내게 적용하는 것은",
    left: "건강한 삶을 위해\n필요하다",
    right: "이미 방법을 아니\n필요가 없다",
  },
  {
    questionId: 17,
    main: "효과적인 자가치료법이 있다면",
    left: "시간과 노력을\n투자할 것이다",
    right: "과정이 힘드니\n포기할 것이다",
  },
];

// 옵션 리스트
const options: QnaAnswerOption[] = [
  { id: 1, text: "매우 동의" },
  { id: 2, text: "동의" },
  { id: 3, text: "동의" },
  { id: 4, text: "매우 동의" },
];

export default function Convention_2_QnA_V6({
  version = "v6",
}: {
  version?: MktVersionTypes;
}) {
  const pageKey = `convention_2`; // 페이지를 구분할 키 값
  const { currentQuestion, qaList, handleSelectAnswer } =
    useQnAOnboardingPromotion({
      pageKey: pageKey,
      questionList: questionList,
      basePath: `/${versionToPathPrefix(version)}-onboarding/convention/2`,
      nextPageRoute: `/${versionToPathPrefix(version)}-onboarding/convention/3`,
      version: version,
    });

  // 훅이 현재 질문을 찾지 못한 경우 (예: URL에 잘못된 ?q=99 입력)에 대한 방어 코드
  if (!currentQuestion) {
    // 실제 프로덕션에서는 에러 페이지로 리디렉션하거나 첫 질문으로 보내는 것이 좋습니다.
    return <div>잘못된 접근입니다.</div>;
  }

  return (
    <Stack
      sx={{
        width: "100%",
        height: "90%",
        py: "20px",
        px: "24px",
        mx: "auto",
      }}
      justifyContent="center"
    >
      <Stack
        sx={{ flex: 1 }}
        justifyContent="center"
        alignItems={"center"}
        spacing={"24px"}
      >
        <SelfCheckQuestion question={currentQuestion} />
        <Stack
          direction="row"
          sx={{ width: "100%" }}
          justifyContent={"space-between"}
        >
          {options.map((option, optionIndex) => (
            <SelectButtonCircleV2
              key={`${currentQuestion.questionId}-${option.id}`}
              questionId={currentQuestion.questionId}
              selected={
                qaList.find(
                  (qa) => qa.questionId === currentQuestion.questionId
                )?.answerId === option.id
              }
              disable={false}
              option={option}
              handleClick={() =>
                handleSelectAnswer(currentQuestion.questionId, option.id)
              }
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
}
